import React, { ComponentType } from 'react';
import ReactDOM from 'react-dom';
import { importRemote } from 'module-federation-import-remote';
import { BridgeComponent, createRemoteComponent } from 'mfe-react-bridge';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import federatedModules from '../../federatedModules';
import { AppShellErrorPage as ErrorPage } from './ErrorPage';

const queryClient = new QueryClient();

const renderErrorPage = (error: Error, attempt = 0) => {
  const container = document.getElementById('error-container');
  if (!container && attempt < 3) {
    setTimeout(() => renderErrorPage(error, attempt + 1), 500);
    return;
  }

  if (container) {
    ReactDOM.render(
      <QueryClientProvider client={queryClient}>
        <ErrorPage errorCode={(error as CustomHttpError).statusCode} />
      </QueryClientProvider>,
      container,
    );
  }
};

const bridgeFallback = (info: { error: Error }): JSX.Element => {
  renderErrorPage(info.error);
  return <div id="error-container" />;
};

type PageProps = {
  Component: React.ComponentType<ModuleComponentProps>;
  path: string;
  hideIntercom?: boolean;
  customAuth?: boolean;
  sentryDsn?: string;
  loadBeforePortal?: boolean;
  configcatProjectKey?: string;
  activeProduct?: string;
  hasSiteNav?: boolean;
  events?: {
    listening: (keyof AppEvents)[];
  };
};

interface RemoteComponent {
  default: ComponentType<ModuleComponentProps>;
  BRIDGE: undefined;
}

interface RemoteBridgeComponent {
  default: () => BridgeComponent<ModuleComponentProps>;
  BRIDGE: true;
}

type Remote = RemoteComponent | RemoteBridgeComponent;

// We need to make `federatedModules` a TS module to fix this properly
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const pages: PageProps[] = federatedModules.map(
  ({ packageName, name, component, allowNonBridge = false, ...props }) => ({
    Component: React.lazy(async () => {
      const remote = await importRemote<Remote>({
        url: `/bundles/${name}`,
        scope: packageName,
        module: component,
      });

      // allow plain component for legacy apps
      if (allowNonBridge && !remote.BRIDGE) {
        return remote;
      }

      // bridge required for all new apps
      if (!remote.BRIDGE) {
        throw new Error(
          `[App Shell] "${packageName}" is missing a bridge. Wrap it with mfe-react-bridge.`,
        );
      }

      return {
        default: createRemoteComponent({
          remote,
          fallback: bridgeFallback,
        }),
      };
    }),
    ...props,
  }),
);

export default pages;
